import { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
} from "@mui/material";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTheme } from "@mui/styles";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import TableHeader from "../table-header";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import { staticAssumptionService } from "../../utils/services/staticAssumptionService";
import StaticAssumptionDetailsModal from "../static-assumption-details-modal";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    padding: "6px",
    textAlign: "start",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "4px",
    textAlign: "start",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const StaticAssumptionTableCell = ({
  staticAssumption,
  handleViewClick,
  handleEditClick,
  handleDeleteClick,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleActionClick = (action) => {
    handlePopoverClose();
    switch (action) {
      case "view":
        handleViewClick(staticAssumption);
        break;
      case "edit":
        handleEditClick(staticAssumption);
        break;
      case "delete":
        handleDeleteClick(staticAssumption);
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      <IconButton
        aria-label="more actions"
        onClick={handlePopoverOpen}
        sx={{
          color: open ? "primary.main" : "inherit",
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", padding: 1 }}>
          <Button
            onClick={() => handleActionClick("view")}
            sx={{ textTransform: "none", justifyContent: "flex-start" }}
          >
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <ViewIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                View
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => handleActionClick("edit")}
            sx={{ textTransform: "none", justifyContent: "flex-start" }}
          >
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <EditIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <IconButton
            onClick={() => handleActionClick("delete")}
            title="Delete"
          >
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <DeleteIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                Delete
              </Typography>
            </Box>
          </IconButton>
        </Box>
      </Popover>
    </Box>
  );
};

const AssumptionDisplayKeys = {
  q1_days: "Days in Q1",
  q2_days: "Days in Q2",
  q3_days: "Days in Q3",
  q4_days: "Days in Q4",
  coc_percentage: "COC %",
  default_dpo_days: "Default DPO Days",
  in_scope_percentage: "In Scope %",
  begining_cycle_inventory_percentage: "Beginning Cycle Inventory %",
  safety_stock_reduction_rl_percentage: "Safety Stock Reduction RL %",
  markdown_reduction_rl_percentage: "Markdown Reduction RL %",
  receipt_schedule_rl_q1_percentage: "Receipt Schedule RL (Q1) %",
  receipt_schedule_rl_q2_percentage: "Receipt Schedule RL (Q2) %",
  receipt_schedule_rl_q3_percentage: "Receipt Schedule RL (Q3) %",
  receipt_schedule_rl_q4_percentage: "Receipt Schedule RL (Q4) %",
  receipt_schedule_current_2_buy_q1_percentage:
    "Receipt Schedule Current 2 Buy (Q1) %",
  receipt_schedule_current_2_buy_q2_percentage:
    "Receipt Schedule Current 2 Buy (Q2) %",
  receipt_schedule_current_2_buy_q3_percentage:
    "Receipt Schedule Current 2 Buy (Q3) %",
  receipt_schedule_current_2_buy_q4_percentage:
    "Receipt Schedule Current 2 Buy (Q4) %",
};

const StaticAssumptionList = ({
  data,
  onEditData,
  onDeleteData,
  viewMode,
  selectedStaticAssumption,
  setSelectedStaticAssumption,
  filterData,
  currentPage,
  itemsPerPage,
}) => {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [staticAssumptionItems, setStaticAssumptionItems] = useState([]);

  const startIndex = currentPage * itemsPerPage;

  const confirmDelete = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await staticAssumptionService.delete(selectedStaticAssumption._id);
      onDeleteData();
      setOpenConfirmation(false);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (staticAssumption) => {
    setSelectedStaticAssumption(staticAssumption);
    setOpenViewModal(true);
  };

  const handleDeleteClick = (staticAssumption) => {
    setSelectedStaticAssumption(staticAssumption);
    setOpenConfirmation(true);
  };

  useEffect(() => {
    setStaticAssumptionItems(
      data?.map((staticAssumption) => ({
        ...staticAssumption,
        action: (
          <StaticAssumptionTableCell
            staticAssumption={staticAssumption}
            handleViewClick={handleViewClick}
            handleEditClick={onEditData}
            handleDeleteClick={handleDeleteClick}
          />
        ),
      })),
    );
  }, [data]);

  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_product.png"
        alt="No data available"
        style={{ objectFit: "cover", height: "300px" }}
      />
    </div>
  );

  const renderGridView = () => (
    <Grid
      sx={{
        marginTop: "1px",
        maxHeight: "100%",
        paddingBottom: 5,
        overflow: "auto",
      }}
      container
      spacing={2}
    >
      {staticAssumptionItems?.map((staticAssumption, index) => (
        <Grid sx={{ paddingBottom: 1 }} item xs={12} sm={6} md={4} key={index}>
          <Card
            sx={{
              boxShadow: 3,
              cursor: "pointer",
              width: "100%",
              height: "100%",
              position: "relative",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
            }}
          >
            <CardContent>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "start" }}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                >
                  {startIndex + index + 1}
                </Typography>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                  }}
                >
                  {staticAssumption?.action}
                </div>
              </div>
              <Grid container spacing={1} mt={2}>
                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    KEY
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Typography
                    noWrap
                    sx={{ textAlign: "start" }}
                    fontWeight="500"
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    :{" "}
                    {AssumptionDisplayKeys[staticAssumption?.key] ||
                      staticAssumption?.key}
                  </Typography>
                </Grid>

                <Grid item xs={5} display={"flex"} alignItems={"center"}>
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    VALUE
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography
                    noWrap
                    sx={{ textAlign: "start" }}
                    fontWeight="500"
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    : {String(staticAssumption?.value).substring(0, 30)}
                    {String(staticAssumption?.value).length > 30 ? "..." : ""}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "key",
      title: "KEY",
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {AssumptionDisplayKeys[item.key] || item.key}
        </Typography>
      ),
    },
    {
      key: "value",
      title: "VALUE",
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {String(item.value).substring(0, 50)}
          {String(item.value).length > 50 ? "..." : ""}
        </Typography>
      ),
    },
    { key: "action", title: "", width: "3%", displayKey: "action" },
  ];

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
        paddingBottom: 20,
        marginTop: 5,
      }}
    >
      {isMobile() ? (
        renderGridView()
      ) : (
        <TableContainer
          sx={{
            overflowY: "auto",
            paddingInline: 0.4,
            maxHeight: "100%",
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{
              border: "1px solid var(--border-color)",
            }}
          >
            <TableHead
              sx={{
                backgroundColor: "var(--table-header-bg) !important",
                "& th": { backgroundColor: "var(--table-header-bg)" },
              }}
            >
              <StyledTableRow>
                {TableSchema.map((column) => (
                  <TableHeader
                    key={column.key}
                    column={column}
                    filterData={filterData}
                    filterKey={column.filterKey}
                    handleOpenFilter={column.handleOpenFilter}
                    title={column.title}
                  />
                ))}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {staticAssumptionItems.length === 0 ? (
                <StyledTableRow>
                  <TableCell colSpan={8} align="center">
                    <NoDataImage />
                  </TableCell>
                </StyledTableRow>
              ) : (
                staticAssumptionItems?.map((x, rowIndex) => (
                  <StyledTableRow
                    sx={{
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      height: 48,
                    }}
                    key={rowIndex}
                  >
                    {TableSchema.map((column, colIndex) => (
                      <StyledTableCell
                        key={column.key || colIndex}
                        sx={{ width: column.width }}
                      >
                        {column.displayKey ? (
                          <Typography
                            fontSize="12px"
                            color="var(--text-secondary)"
                            fontWeight={400}
                            fontFamily={"Poppins"}
                          >
                            {x[column.displayKey]}
                          </Typography>
                        ) : (
                          column.render(x, rowIndex)
                        )}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );

  const theme = useTheme();

  return (
    <div sx={{ minWidth: "100%" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <div
          style={{
            overflowY: "scroll",
            display: "flex",
            maxHeight: isMobile()
              ? `calc(100vh - 210px)`
              : `calc(100vh - 250px)`,
            width: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::WebkitScrollbar": { width: 0 },
          }}
        >
          {viewMode === "grid" ? renderGridView() : renderListView()}
        </div>
      )}

      <DeleteConfirmationModal
        DeleteConfirmationModal
        title={`Are you sure you want to delete the selected static assumption?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={(e) => confirmDelete(e)}
      />

      <StaticAssumptionDetailsModal
        staticAssumption={selectedStaticAssumption}
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
      />
    </div>
  );
};

export default StaticAssumptionList;
